import React, { useState } from 'react';
import WorkflowProgressIndicator from '../../components/ui/WorkflowProgressIndicator';
import ExportQueueIndicator from '../../components/ui/ExportQueueIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ExportCard from './components/ExportCard';
import ScheduledExportsPanel from './components/ScheduledExportsPanel';
import ExportHistoryPanel from './components/ExportHistoryPanel';
import BulkExportPanel from './components/BulkExportPanel';

const ExportAndReportsCenter = () => {
  const [activeTab, setActiveTab] = useState('exports');
  const [generatingReports, setGeneratingReports] = useState({});
  const [queueCount, setQueueCount] = useState(2);
  const [completedCount, setCompletedCount] = useState(3);

  const reportTypes = [
    {
      reportType: 'best_price',
      title: 'Best Price Analysis',
      description: 'Export products with the lowest available prices from all suppliers. Includes confidence scores, supplier details, and stock availability for procurement decision-making.',
      estimatedSize: '2.4 MB',
      sampleColumns: [
        { key: 'product_name', label: 'Product Name' },
        { key: 'brand', label: 'Brand' },
        { key: 'mpn', label: 'MPN' },
        { key: 'ean', label: 'EAN/UPC' },
        { key: 'best_price', label: 'Best Price' },
        { key: 'supplier', label: 'Best Supplier' },
        { key: 'stock_level', label: 'Stock Level' },
        { key: 'confidence', label: 'Match Confidence' },
        { key: 'alternative_count', label: 'Alternative Suppliers' }
      ]
    },
    {
      reportType: 'all_groups',
      title: 'Complete Product Groups',
      description: 'Comprehensive export of all matched product groups showing price comparisons across suppliers. Includes detailed matching information and supplier performance metrics.',
      estimatedSize: '8.7 MB',
      sampleColumns: [
        { key: 'group_id', label: 'Group ID' },
        { key: 'product_name', label: 'Product Name' },
        { key: 'brand', label: 'Brand' },
        { key: 'supplier_name', label: 'Supplier' },
        { key: 'price', label: 'Price' },
        { key: 'stock', label: 'Stock' },
        { key: 'match_type', label: 'Match Type' },
        { key: 'confidence_score', label: 'Confidence' },
        { key: 'last_updated', label: 'Last Updated' }
      ]
    },
    {
      reportType: 'ambiguous',
      title: 'Ambiguous Matches',
      description: 'Products requiring manual review due to uncertain matching. Export includes similarity scores, potential matches, and recommended actions for procurement teams.',
      estimatedSize: '1.2 MB',
      sampleColumns: [
        { key: 'product_a', label: 'Product A' },
        { key: 'product_b', label: 'Product B' },
        { key: 'similarity_score', label: 'Similarity Score' },
        { key: 'match_reason', label: 'Match Reason' },
        { key: 'price_difference', label: 'Price Difference' },
        { key: 'supplier_a', label: 'Supplier A' },
        { key: 'supplier_b', label: 'Supplier B' },
        { key: 'review_status', label: 'Review Status' },
        { key: 'recommended_action', label: 'Recommendation' }
      ]
    },
    {
      reportType: 'unmatched',
      title: 'Unmatched Products',
      description: 'Products that could not be matched across suppliers. Useful for identifying unique offerings and potential data quality issues in supplier catalogs.',
      estimatedSize: '456 KB',
      sampleColumns: [
        { key: 'product_name', label: 'Product Name' },
        { key: 'brand', label: 'Brand' },
        { key: 'mpn', label: 'MPN' },
        { key: 'ean', label: 'EAN/UPC' },
        { key: 'supplier', label: 'Supplier' },
        { key: 'price', label: 'Price' },
        { key: 'category', label: 'Category' },
        { key: 'reason_unmatched', label: 'Unmatched Reason' }
      ]
    }
  ];

  const tabs = [
    { id: 'exports', label: 'Export Reports', icon: 'Download' },
    { id: 'bulk', label: 'Bulk Export', icon: 'Package' },
    { id: 'scheduled', label: 'Scheduled Exports', icon: 'Calendar' },
    { id: 'history', label: 'Export History', icon: 'History' },
    { id: 'analytics', label: 'Analytics Reports', icon: 'BarChart' },
    { id: 'suppliers', label: 'Supplier Reports', icon: 'Users' }
  ];

  const handleGenerateReport = (config) => {
    setGeneratingReports(prev => ({ ...prev, [config?.reportType]: true }));
    setQueueCount(prev => prev + 1);
    
    // Simulate report generation
    setTimeout(() => {
      setGeneratingReports(prev => ({ ...prev, [config?.reportType]: false }));
      setQueueCount(prev => Math.max(0, prev - 1));
      setCompletedCount(prev => prev + 1);
    }, 3000);
  };

  const handleDownloadCompleted = (index) => {
    console.log('Downloading completed report:', index);
  };

  const handleClearCompleted = () => {
    setCompletedCount(0);
  };

  const processingExport = Object.keys(generatingReports)?.find(key => generatingReports?.[key]) 
    ? { name: `${Object.keys(generatingReports)?.find(key => generatingReports?.[key])}_report.xlsx`, progress: 65 }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Export and Reports Center</h1>
                <p className="text-muted-foreground">
                  Generate comprehensive analysis reports and manage export operations for procurement decision-making
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <ExportQueueIndicator
                  queueCount={queueCount}
                  completedCount={completedCount}
                  processingExport={processingExport}
                  onDownloadClick={handleDownloadCompleted}
                  onClearCompleted={handleClearCompleted}
                />
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => window.location.href = '/results-dashboard'}
                >
                  Back to Results
                </Button>
              </div>
            </div>
            
            <WorkflowProgressIndicator 
              currentStep={5}
              completedSteps={['upload', 'configure', 'process', 'analyze', 'review']}
            />
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-6">
            <nav className="flex space-x-8">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`
                    flex items-center space-x-2 px-4 py-4 text-sm font-medium border-b-2 transition-colors
                    ${activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name={tab?.icon} size={16} />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          {activeTab === 'exports' && (
            <div>
              {/* Quick Actions */}
              <div className="mb-8 p-6 bg-card border border-border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Quick Export Actions</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Generate individual reports or download all data at once
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="outline"
                      iconName="Eye"
                      iconPosition="left"
                      onClick={() => window.location.href = '/results-dashboard'}
                    >
                      Preview Data
                    </Button>
                    <Button
                      variant="default"
                      iconName="Package"
                      iconPosition="left"
                      onClick={() => setActiveTab('bulk')}
                    >
                      Bulk Export
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-3 bg-muted bg-opacity-50 rounded">
                    <div className="text-2xl font-bold text-primary">1,247</div>
                    <div className="text-muted-foreground">Total Products</div>
                  </div>
                  <div className="text-center p-3 bg-muted bg-opacity-50 rounded">
                    <div className="text-2xl font-bold text-success">892</div>
                    <div className="text-muted-foreground">Matched Products</div>
                  </div>
                  <div className="text-center p-3 bg-muted bg-opacity-50 rounded">
                    <div className="text-2xl font-bold text-warning">127</div>
                    <div className="text-muted-foreground">Need Review</div>
                  </div>
                  <div className="text-center p-3 bg-muted bg-opacity-50 rounded">
                    <div className="text-2xl font-bold text-error">228</div>
                    <div className="text-muted-foreground">Unmatched</div>
                  </div>
                </div>
              </div>

              {/* Export Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {reportTypes?.map((report) => (
                  <ExportCard
                    key={report?.reportType}
                    reportType={report?.reportType}
                    title={report?.title}
                    description={report?.description}
                    estimatedSize={report?.estimatedSize}
                    sampleColumns={report?.sampleColumns}
                    onGenerate={handleGenerateReport}
                    isGenerating={generatingReports?.[report?.reportType] || false}
                  />
                ))}
              </div>
            </div>
          )}

          {activeTab === 'bulk' && (
            <BulkExportPanel />
          )}

          {activeTab === 'scheduled' && (
            <ScheduledExportsPanel />
          )}

          {activeTab === 'history' && (
            <ExportHistoryPanel />
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Analytics Reports</h2>
                    <p className="text-muted-foreground mt-1">
                      Generate detailed analytics and insights reports for strategic decision making
                    </p>
                  </div>
                  <Button variant="outline" iconName="RefreshCw">
                    Refresh Data
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Price Trend Analysis */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-primary bg-opacity-20 rounded">
                          <Icon name="TrendingUp" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Price Trend Analysis</h3>
                          <p className="text-sm text-muted-foreground">Historical pricing patterns and forecasts</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Time Range:</span>
                        <span className="text-foreground">Last 6 months</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Products Tracked:</span>
                        <span className="text-foreground">1,247</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~3.2 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Market Share Analysis */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-success bg-opacity-20 rounded">
                          <Icon name="PieChart" size={20} className="text-success" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Market Share Analysis</h3>
                          <p className="text-sm text-muted-foreground">Supplier market positioning and coverage</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Suppliers Analyzed:</span>
                        <span className="text-foreground">23</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Market Coverage:</span>
                        <span className="text-foreground">94.2%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~1.8 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Cost Savings Report */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-warning bg-opacity-20 rounded">
                          <Icon name="DollarSign" size={20} className="text-warning" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Cost Savings Report</h3>
                          <p className="text-sm text-muted-foreground">Potential and realized procurement savings</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Potential Savings:</span>
                        <span className="text-foreground font-semibold text-success">$127,430</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Analysis Period:</span>
                        <span className="text-foreground">Current Quarter</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~950 KB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Performance Metrics */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-info bg-opacity-20 rounded">
                          <Icon name="Activity" size={20} className="text-info" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Performance Metrics</h3>
                          <p className="text-sm text-muted-foreground">System and matching algorithm performance</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Match Accuracy:</span>
                        <span className="text-foreground">96.8%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Processing Speed:</span>
                        <span className="text-foreground">1,200 items/min</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~750 KB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'suppliers' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Supplier Reports</h2>
                    <p className="text-muted-foreground mt-1">
                      Comprehensive supplier analysis and performance reporting
                    </p>
                  </div>
                  <Button variant="outline" iconName="RefreshCw">
                    Refresh Data
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Supplier Performance */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-primary bg-opacity-20 rounded">
                          <Icon name="Star" size={20} className="text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Supplier Performance</h3>
                          <p className="text-sm text-muted-foreground">Rating and KPI analysis by supplier</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Active Suppliers:</span>
                        <span className="text-foreground">23</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Top Performer:</span>
                        <span className="text-foreground">TechDistributor Pro</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~2.1 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Catalog Coverage */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-success bg-opacity-20 rounded">
                          <Icon name="Grid" size={20} className="text-success" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Catalog Coverage</h3>
                          <p className="text-sm text-muted-foreground">Product coverage and gaps by supplier</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Total Products:</span>
                        <span className="text-foreground">1,247</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Coverage Rate:</span>
                        <span className="text-foreground">87.3%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~1.5 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Price Competitiveness */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-warning bg-opacity-20 rounded">
                          <Icon name="Target" size={20} className="text-warning" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Price Competitiveness</h3>
                          <p className="text-sm text-muted-foreground">Comparative pricing analysis across suppliers</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Price Points:</span>
                        <span className="text-foreground">4,891</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Best Price Rate:</span>
                        <span className="text-foreground">34.2%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~2.7 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>

                  {/* Compliance & Quality */}
                  <div className="bg-muted bg-opacity-30 border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-info bg-opacity-20 rounded">
                          <Icon name="Shield" size={20} className="text-info" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground">Compliance & Quality</h3>
                          <p className="text-sm text-muted-foreground">Data quality and compliance metrics</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Quality Score:</span>
                        <span className="text-foreground">8.7/10</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Compliance Rate:</span>
                        <span className="text-foreground">98.1%</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Report Size:</span>
                        <span className="text-foreground">~1.3 MB</span>
                      </div>
                    </div>
                    <Button className="w-full" variant="outline" iconName="Download">
                      Generate Report
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="bg-card border-t border-border">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Last data update: October 17, 2025 at 2:30 PM
              </div>
              <div className="flex space-x-3">
                <Button
                  variant="ghost"
                  iconName="RefreshCw"
                  iconPosition="left"
                >
                  Refresh Data
                </Button>
                <Button
                  variant="outline"
                  iconName="Settings"
                  iconPosition="left"
                >
                  Export Settings
                </Button>
                <Button
                  variant="default"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => window.location.href = '/file-upload-dashboard'}
                >
                  New Analysis
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExportAndReportsCenter;